import { IBillingData } from "api/interfaces/megaERP";
import {
  Unsubscribe,
  addDoc,
  deleteDoc,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
} from "firebase/firestore";
import MeasurementModel from "../models/MeasurementModel";
import {
  fromDocumentData,
  getCollectionRef,
  getDocumentRef,
  onCollectionSnapshot,
  onDocumentSnapshot,
  setDocument,
  toDocumentData,
  updateDocument,
} from "./FirebaseRepository";

class MeasurementRepository {
  private path = (contractUid: string) =>
    `cfContract/${contractUid}/measurements`;

  private pathBilling = (contractUid: string, measurementUid: string) =>
    `cfContract/${contractUid}/measurements/${measurementUid}/billing`;

  public async addMeasurementToDataBase(
    data: MeasurementModel
  ): Promise<string> {
    const collection = getCollectionRef(
      this.path(data.contractUid),
      converter(data.contractUid)
    );
    const doc = await addDoc(collection, data);
    return doc.id;
  }

  async deleteMeasurementFromDataBase(
    contractUid: string,
    uid: string
  ): Promise<void> {
    const doc = getDocumentRef(this.path(contractUid), uid);
    return deleteDoc(doc);
  }

  public updateMeasurementOnDataBase(
    contractUid: string,
    uid: string,
    updatedData: Partial<MeasurementModel>,
    transaction?: any
  ) {
    const doc = getDocumentRef(this.path(contractUid), uid);
    const data = converter(contractUid).toFirestore(updatedData);
    return updateDocument(doc, data, transaction);
  }

  async updateMeasurementBillingDataOnDataBase(
    contractUid: string,
    measurementUid: string,
    updatedData: IBillingData
  ) {
    const doc = getDocumentRef(
      this.pathBilling(contractUid, measurementUid),
      "lastDataSent"
    );
    const data = toDocumentData(updatedData);
    return setDocument(doc, data);
  }

  async getMeasurementBillingDataFromDataBase(
    contractUid: string,
    measurementUid: string
  ): Promise<IBillingData | undefined> {
    const doc = getDocumentRef(
      this.pathBilling(contractUid, measurementUid),
      "lastDataSent"
    );
    return getDoc(doc).then((snapshot) =>
      snapshot.exists()
        ? ({ ...snapshot.data(), measurementUid } as IBillingData)
        : undefined
    );
  }

  async getAllMeasurementsFromDataBase(
    contractUid: string
  ): Promise<MeasurementModel[]> {
    const collection = getCollectionRef(
      this.path(contractUid),
      converter(contractUid)
    );
    return getDocs(collection).then((snapshot) =>
      snapshot.docs.map((doc) => doc.data() as MeasurementModel)
    );
  }

  async getLastCodeOfMeasurements(contractUid: string): Promise<number> {
    const collection = getCollectionRef(
      this.path(contractUid),
      converter(contractUid)
    );
    const queryRef = query(collection, orderBy("code", "desc"), limit(1));
    const data = await getDocs(queryRef).then((snapshot) => snapshot.docs);

    if (data.length) return (data[0].data() as MeasurementModel).code;
    return 0;
  }

  addMeasurementCollectionSnapshot(
    contractUid: string,
    onCreate: (data: MeasurementModel[]) => void,
    onChange: (data: MeasurementModel[]) => void,
    onRemove: (data: MeasurementModel[]) => void
  ): Unsubscribe {
    const collection = getCollectionRef(
      this.path(contractUid),
      converter(contractUid)
    );

    return onCollectionSnapshot<MeasurementModel>(
      collection,
      onCreate,
      onChange,
      onRemove,
      (error) => console.error("MeasurementsSnapshot", error)
    );
  }

  addMeasurementDocumentSnapshot(
    contractUid: string,
    uid: string,
    onCreate: (data: MeasurementModel) => void,
    onChange: (data: MeasurementModel) => void
  ): Unsubscribe {
    const doc = getDocumentRef(
      this.path(contractUid),
      uid,
      converter(contractUid)
    );
    return onDocumentSnapshot(doc, onCreate, onChange, (error) =>
      console.error("MeasurementSnapshot", error)
    );
  }
}

// Firestore data converter
const converter = (contractUid) => ({
  toFirestore: (measurement: Partial<MeasurementModel>) => {
    const data =
      measurement instanceof MeasurementModel
        ? measurement.toSave()
        : measurement;
    return toDocumentData(data);
  },
  fromFirestore: (snapshot, options) => {
    let data = snapshot.data(options);
    data = fromDocumentData(data);

    if (data.billingOrder?.invoices)
      data.billingOrder.invoices = data.billingOrder.invoices.map((invoice) =>
        fromDocumentData(invoice)
      );

    return MeasurementModel.create(snapshot.id, {
      ...data,
      contractUid,
    });
  },
});

export default new MeasurementRepository();
